import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { AUTH_STATE, AUTH_STATE_KEY, LANGUAGE, STARTMODE_WHEN_ROUTING } from '../../define/global';
import { getWhaleSpaceTokenInfo } from '../../extApis/whaleSpaceApi';
import { setWhaleSpaceData } from '../../store/modules/whaleSpaceData';
import { useTranslation } from 'react-i18next';
import WhaleAuth from '../common/WhaleAuth';
import { WhaleSpaceError } from '../../errors';
import ErrorPage from './ErrorPage';
import ga from '../../utils/gaConnect';

import progress_loop from '../../img/progress_loop.png';
import { getNaverWorksTokenInfo, reauthorizationNaverWorks } from '../../extApis/naverWorksApi';
import { initNaverWorksData, setNaverWorksData } from '../../store/modules/naverWorksData';
import { getPWCCurrentLoginStatus } from '../plugin/pwc/pwcApis';
import { initPWCConnectedData, setPWCConnectedData } from '../../store/modules/pwcAuthData';
import { getLanguage } from '../../utils/util';

const Iframe = styled.iframe`
  height: 100%;
  width: 100%;
  overflow: hidden;
  margin: 0;
  border: 0;
`;

const play = keyframes`
100% {background-position : -1900px;}
`;

const AnimationBar = styled.div`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  background-image: url(${progress_loop});
  animation: ${play} 0.9s steps(19) infinite;
  transform: translate(0, -10px) scale(1.5, 1.5);
`;

const WaitingMsg = styled.span`
  font-size: 24px;
`;

const WaitingWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const AsyncRendering = ({ children, match }) => {
  //init or noAuth or Auth
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [authState, setAuthState] = useState(AUTH_STATE.AUTH_INIT);

  const selectByAuthState = (authState, children, onAuthSuccess) => {
    switch (authState) {
      case AUTH_STATE.AUTH_NO:
        return (
          <WhaleAuth />
          // <>{t('AsyncAuth.authenticating')}</>
          /*  <Iframe
            title="WhaleAuth"
            src="/api/v1/auth/whaleSpace/reAuthorization"
            onLoad={(e) => {
              try {
                const location = e.currentTarget.contentWindow.location;
                if (location.pathname.endsWith('authOk')) onAuthSuccess();
              } catch (e) {}
            }}
          /> */
        );
      case AUTH_STATE.AUTH_SUCCESS:
        return children;
      case AUTH_STATE.AUTH_FAIL:
        // return <>{t('AsyncAuth.authFail')}</>;
        return <ErrorPage msg={t('AsyncAuth.authFail')} />;
      case AUTH_STATE.AUTH_BLOCK_POPUP:
        return <>{t('AsyncAuth.authPopupBlock')}</>;
      case AUTH_STATE.AUTH_INIT:
        return (
          <WaitingWrapper>
            <AnimationBar width="100px" height="40px"></AnimationBar>
            <WaitingMsg>{t('WhaleAuthSplash.waitingMsg')} </WaitingMsg>
          </WaitingWrapper>
        );

      default:
        break;
    }
  };

  const convUserType = (str) => {
    if (!str || typeof str !== 'string') return '';
    const type = str.toLowerCase();
    switch (type) {
      case 'stu':
        return t('UserInfoDialog.Student');
      case 'tea':
        return t('UserInfoDialog.Teacher');
      case 'emp':
        return t('UserInfoDialog.Employee');
      case 'par':
        return t('UserInfoDialog.Parent');
      default:
        break;
    }
    return '';
  };

  const authProcess = async ({ match }) => {
    try {
      const res = await getWhaleSpaceTokenInfo();
      let userSpaceName = '';
      const org = res?.data?.userInfo?.organizations;
      if (org && org.length > 0) {
        userSpaceName = org[0]?.organizationName || '';
      }

      const language = getLanguage(i18n.language);

      const userFullName =
        language === LANGUAGE.KO
          ? `${res?.data?.userInfo?.userName?.lastName || ''} ${
              res?.data?.userInfo?.userName?.firstName || ''
            }`
          : `${res?.data?.userInfo?.userName?.firstName || ''} ${
              res?.data?.userInfo?.userName?.lastName || ''
            }`;

      const userEmail = res?.data?.userInfo?.email || '';
      const userType = res?.data?.userInfo?.userTypeCode || '';
      const domain = userEmail?.split('@')?.pop();
      // ga.event({ category: '로그인', action: '조직', label: userSpaceName });
      // ga.event({ category: '로그인', action: '도메인', label: userEmail?.split('@')?.pop() || '' });
      ga.event({
        category: '로그인',
        action: userEmail?.split('@')?.pop() || '',
        label: userSpaceName
      });
      dispatch(
        setWhaleSpaceData({
          userSpaceName,
          userFullName,
          userEmail,
          userType: convUserType(userType),
          isAuthrization: true
        })
      );

      const isMultiAuth =
        match.params.startMode === STARTMODE_WHEN_ROUTING.NEW_DOC_FROM_NAVERWORKS ||
        match.params.startMode === STARTMODE_WHEN_ROUTING.OPEN_DOC_FROM_ITT ||
        match.params.startMode === STARTMODE_WHEN_ROUTING.OPEN_DOC_FROM_NAVERWORKS;

      try {
        const res2 = await getNaverWorksTokenInfo();
        dispatch(
          setNaverWorksData({
            userFullName: `${res2.data.userInfo.userName.lastName} ${res2.data.userInfo.userName.firstName}`,
            userEmail: res2.data.userInfo.email
          })
        );
      } catch (error) {
        if (error.code === 401 && isMultiAuth) {
          reauthorizationNaverWorks({ domain, isSelf: true });
          return;
        } else {
          dispatch(initNaverWorksData());
        }
      }

      try {
        await getPWCCurrentLoginStatus();
        dispatch(
          setPWCConnectedData({
            isConnected: true
          })
        );
      } catch (error) {
        dispatch(initPWCConnectedData());
      }

      setAuthState(AUTH_STATE.AUTH_SUCCESS);
    } catch (error) {
      if (error instanceof WhaleSpaceError && error.code === 401) {
        setAuthState(AUTH_STATE.AUTH_NO);
      } else {
        setAuthState(AUTH_STATE.AUTH_FAIL);
      }
    }
  };

  useEffect(() => {
    authProcess({ match });
  }, [match]);

  const onAuthSuccess = useCallback(() => {
    setAuthState(AUTH_STATE.AUTH_SUCCESS);
  }, []);

  return <>{selectByAuthState(authState, children, onAuthSuccess)}</>;
};

export default AsyncRendering;
